import { CoreApi } from "./coreApi.js";
/**
 * One cache for the master data every screen joins against.
 *
 * `client.ts` sends `cache: "no-store"` on every GET, deliberately — a read right after a write
 * must not come back from the browser cache. But that also means categories, brands, collections
 * and the storefront variant list are refetched in full every time a pane becomes visible, and
 * several of them are large: `storefrontVariants` is a 20 000-row read that the Product Dashboard
 * and the Product List each issued independently, on the same page load, for the same data.
 *
 * What was already here and why it was not enough: `inventoryApi.listBalance` has a 15 s promise
 * cache, but it is a SINGLE SLOT keyed by the query — two panes paging the same table with
 * different offsets evict each other on every call, so the hit rate collapses to nearly zero
 * exactly when it is needed most. This keeps a `Map` keyed by the query, so concurrent readers with
 * different arguments coexist.
 *
 * TTL is five minutes: this is data that changes when someone edits the catalogue, not per
 * transaction. Writes must call {@link invalidate} — that is what makes a five-minute TTL safe
 * rather than a five-minute window of stale screens.
 */
const TTL_MS = 5 * 60_000;
const cache = new Map();
function memo(key, load) {
    const now = Date.now();
    const hit = cache.get(key);
    if (hit && now - hit.at <= TTL_MS)
        return hit.p;
    const p = load();
    // Never memoize a rejection: one transient failure would otherwise be replayed to every caller
    // for the whole TTL, turning a blip into five minutes of a broken screen.
    p.catch(() => { if (cache.get(key)?.p === p)
        cache.delete(key); });
    cache.set(key, { at: now, p });
    return p;
}
const itemsOf = (res) => {
    const d = res?.data;
    return Array.isArray(d) ? d : [];
};
export const RefData = {
    /** core.material categories — flat list. */
    categories: (limit = 200) => memo(`categories:${limit}`, async () => itemsOf(await CoreApi.listCategories({ limit }))),
    /** The category tree, for resolving a categoryCode to a display path. */
    categoryTree: () => memo("categoryTree", async () => itemsOf(await CoreApi.categoryTree())),
    /** brandCode → name. A material row carries only the code. */
    brands: (limit = 500) => memo(`brands:${limit}`, async () => itemsOf(await CoreApi.listBrands({ limit }))),
    /** Merchandising collections. */
    collections: (limit = 200) => memo(`collections:${limit}`, async () => itemsOf(await CoreApi.listCollections({ limit }))),
    /**
     * The whole storefront variant list.
     *
     * This is the read that most needed a home: 20 000 rows, issued by productdashboard.ts and
     * productproducts.ts independently on the same page load. Both now hit one in-flight promise.
     */
    storefrontVariants: (limit = 20000) => memo(`storefrontVariants:${limit}`, async () => itemsOf(await CoreApi.storefrontVariants({ limit }))),
    /**
     * The barcode master, whole. ~1 200 rows on this estate against 20 000 variants, because a
     * barcode exists only where a label has been printed — the ceiling matches the variant read
     * beside it so the two cannot disagree about what "the whole catalogue" means.
     *
     * Cached here for the same reason as the variant list: the Product List joins it to search by
     * the number on a tag, and any other screen that grows a barcode box gets the same one read.
     */
    barcodes: (limit = 20000) => memo(`barcodes:${limit}`, async () => itemsOf(await CoreApi.listBarcodes({ limit }))),
};
/**
 * Drop cached master data after a write that changes it.
 *
 * `invalidate()` with no argument clears everything — the safe default for a save whose blast
 * radius is not obvious. Pass a prefix (`"brands"`, `"storefrontVariants"`) to be surgical.
 */
export function invalidate(prefix) {
    if (!prefix) {
        cache.clear();
        return;
    }
    for (const k of [...cache.keys()])
        if (k === prefix || k.startsWith(prefix + ":"))
            cache.delete(k);
}
/**
 * A per-key cache bounded BOTH ways — the same `{at, value}` shape `memo()` above keeps privately,
 * exposed as a small `Map`-shaped class for callers whose caching has its own priming/invalidation
 * logic and cannot go through a bare get-or-load wrapper (media.ts's `materialFactsCache`,
 * inventoryApi.ts's `balanceCache` — both keyed per query/material+branch, both grow one entry per
 * DISTINCT key ever asked for).
 *
 * TTL alone is not enough: a picking screen priming a different mix of materials on every poll, or
 * `fetchPages()` walking a different offset per page, never repeats a key often enough for
 * expiry to bound memory — every entry ages out eventually, but new ones keep arriving. A count
 * cap alone is not enough either: it bounds memory but not staleness, and would go on serving a
 * ten-minute-old price forever on a small, fixed set of keys. Together: an entry is refused once
 * its TTL passes (`get`/`has` both treat it as a miss and drop it), and the OLDEST entry is evicted
 * whenever a `set` would push the map past `maxEntries` — "oldest" by last-touched order, since
 * `set` re-inserts a key at the end of the `Map`'s own iteration order.
 */
export class BoundedCache {
    ttlMs;
    maxEntries;
    map = new Map();
    constructor(ttlMs, maxEntries) {
        this.ttlMs = ttlMs;
        this.maxEntries = maxEntries;
    }
    get(key) {
        const hit = this.map.get(key);
        if (!hit)
            return undefined;
        if (Date.now() - hit.at > this.ttlMs) {
            this.map.delete(key);
            return undefined;
        }
        return hit.v;
    }
    has(key) { return this.get(key) !== undefined; }
    set(key, v) {
        this.map.delete(key); // re-insert at the end, so eviction below is LRU, not FIFO
        this.map.set(key, { at: Date.now(), v });
        while (this.map.size > this.maxEntries) {
            const oldest = this.map.keys().next().value;
            if (oldest === undefined)
                break;
            this.map.delete(oldest);
        }
    }
    delete(key) { this.map.delete(key); }
    clear() { this.map.clear(); }
    /** For tests only — real callers have no business counting entries. */
    get size() { return this.map.size; }
}
//# sourceMappingURL=refdata.js.map